import React, { useEffect, useMemo, useState } from "react";
import { TrendingUp, AlertTriangle, DollarSign, MicOff, Clock } from "lucide-react";
import { apiGet } from "../../../Api/Client.js";
import LoadFailed from "./LoadFailed.jsx";
import { isPlayable, playHref, stamp } from "./CallPlayer.jsx";

// Group III — deal risk. A different surface on purpose.
//
// Groups I and II answer "how do I get better?". They are about the rep and
// they belong on a coaching card. This answers "is this deal real?" — it is
// about the DEAL — and putting it on somebody's personal scorecard turns one
// page into a performance review and a forecast at once, which serves neither
// job. So it lives here, beside the account, and nothing on the rep's card
// links to it.
//
// THE RULE THIS COMPONENT EXISTS TO HOLD: no composite score. There is no
// "deal health: 62" anywhere below, and there must never be, for exactly the
// reason `sentiment: 58` was cut — it invites "62 out of what, and so what?"
// and has no answer. What an AE can act on is WHEN, WHO and WHAT HAPPENED:
// their tone changed at 18:40, right after the annual commitment came up, and
// the person who went quiet is the budget holder. Those three facts are the
// whole product here.
//
// It is also careful about what it does NOT know. The buyer-side signals need
// a confirmed rep speaker label to tell the two sides apart; without one, ECHO
// withholds them rather than guessing which voice is the customer. That
// withholding is rendered as a sentence, never as a zero or an empty chart.

const card = { background: "var(--surface)", border: "1px solid var(--c-200)", borderRadius: 14, padding: "16px 18px" };
const muted = { color: "var(--c-500)" };

const MOMENT_STYLE = {
  price: { icon: DollarSign, color: "var(--blue-600)" },
  engagement_gap: { icon: MicOff, color: "var(--red-600, #c0392b)" },
  cooling: { icon: TrendingUp, color: "var(--red-600, #c0392b)" },
};

function Timeline({ call }) {
  const moments = call.timeline || [];
  const total = call.duration_seconds || 0;
  if (!moments.length) return null;

  return (
    <div style={{ marginTop: 10 }}>
      {/* The track. A call is a line and the flagged moments are positions on
          it — which is the only visual that answers "how far in did this
          happen", the question that makes price timing meaningful at all. */}
      {total > 0 && (
        <div style={{ position: "relative", height: 24, marginBottom: 8 }}>
          <div style={{ position: "absolute", top: 11, left: 0, right: 0, height: 2,
                        background: "var(--c-200)", borderRadius: 999 }} />
          {moments.map((m, i) => {
            const pct = Math.max(0, Math.min(1, (m.at_seconds || 0) / total)) * 100;
            const style = MOMENT_STYLE[m.kind] || {};
            return (
              <span key={i} title={`${stamp(m.at_seconds)} — ${m.text}`}
                    style={{ position: "absolute", left: `${pct}%`, top: 6, width: 12, height: 12,
                             marginLeft: -6, borderRadius: "50%",
                             background: style.color || "var(--c-500)",
                             border: "2px solid var(--surface)" }} />
            );
          })}
          <span style={{ position: "absolute", right: 0, top: 0, fontSize: 9.5, ...muted }}>
            {stamp(total)}
          </span>
        </div>
      )}

      {moments.map((m, i) => {
        const style = MOMENT_STYLE[m.kind] || {};
        const Icon = style.icon || Clock;
        const href = playHref(call.call_link, m.at_seconds);
        return (
          <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "5px 0" }}>
            <Icon size={13} color={style.color || "var(--c-500)"} style={{ marginTop: 2, flexShrink: 0 }} />
            <div style={{ fontSize: 12.5, color: "var(--c-800)", lineHeight: 1.5 }}>
              {href && isPlayable(call.call_link) ? (
                <a href={href} target="_blank" rel="noreferrer"
                   style={{ color: "var(--blue-600)", textDecoration: "none", fontWeight: 600 }}>
                  {stamp(m.at_seconds)}
                </a>
              ) : (
                <strong style={{ color: "var(--c-900)" }}>{stamp(m.at_seconds)}</strong>
              )}
              {" — "}{m.text}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Readings({ signals }) {
  const rows = [];

  if (signals.price_first_mention_pct != null) {
    const pct = Math.round(signals.price_first_mention_pct * 100);
    // `value_before_price` is the senior rubric's commercial_arithmetic
    // behaviour made observable: did they establish the buyer's own numbers
    // before quoting ours. It is stated as the fact it is, not scored.
    const ok = signals.value_before_price;
    rows.push({
      tone: ok === false ? "warn" : "plain",
      text: ok === false
        ? `Price was raised ${pct}% into the call, before the cost of their current setup was established.`
        : `Price came up ${pct}% into the call, after their own numbers were on the table.`,
    });
  } else if (signals.price_first_mention_s === null) {
    rows.push({ tone: "plain", text: "We never named a price on this call." });
  }

  if (signals.cost_discovery_at_seconds != null) {
    rows.push({
      tone: "plain",
      text: `Their own spend came up at ${stamp(signals.cost_discovery_at_seconds)} — that is discovery, not a price reveal.`,
    });
  }

  if (signals.buyer_sentiment_shift === "cooling") {
    rows.push({
      tone: "warn",
      // Engagement, not emotion. Inferring how somebody FEELS from a
      // transcript is the confident guess this whole design refuses; what is
      // measurable is that they were answering in sentences and are now
      // answering in words.
      text: `They were answering in ${signals.buyer_words_per_turn_first_half} words a turn and dropped to `
            + `${signals.buyer_words_per_turn_second_half} in the second half.`
            + (signals.buyer_reduced_to_acknowledgements ? " By the end they were only acknowledging." : ""),
    });
  } else if (signals.buyer_sentiment_shift === "warming") {
    rows.push({
      tone: "good",
      text: `They opened up as the call went on — ${signals.buyer_words_per_turn_first_half} words a turn `
            + `to ${signals.buyer_words_per_turn_second_half}.`,
    });
  }

  (signals.silent_participants || []).forEach((p) => {
    rows.push({
      tone: "warn",
      text: `${p.name || p.speaker} was on the call and never spoke.`,
    });
  });

  // The buyer side, as a count rather than a per-person claim: an external
  // invitee who never speaks leaves no voice to match a name to, so this
  // says HOW MANY, never which ones. "3 invited, 1 spoke" is a real signal
  // (a lot of the buying committee sat this one out) without pretending to
  // know who.
  if (signals.buyer_invite_summary) {
    const { invited, heard_from: heard, external_invitees: named } = signals.buyer_invite_summary;
    if (invited > 0) {
      const missing = heard != null ? invited - heard : null;
      rows.push({
        tone: missing > 0 ? "warn" : "plain",
        text: heard != null
          ? `${invited} invited from their side, ${heard} spoke${missing > 0 ? ` — ${missing} sat silent` : ""}.`
          : `${invited} invited from their side.`,
      });
      if (named?.length) {
        rows.push({
          tone: "plain",
          text: `Invited: ${named.map((p) => p.name).join(", ")}.`,
        });
      }
    }
  }

  if (signals.buyer_sentiment_shift === null && signals._confidence?.withheld) {
    rows.push({
      tone: "plain",
      // Withheld, and said so. A missing signal is recoverable; a wrong one an
      // AE forecast against is not.
      text: `Buyer-side signals aren't shown for this call — ${signals._confidence.withheld}.`,
    });
  }

  if (!rows.length) return null;

  const colour = { warn: "var(--red-600, #c0392b)", good: "var(--green-600)", plain: "var(--c-700)" };
  return (
    <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid var(--c-200)" }}>
      {rows.map((r, i) => (
        <div key={i} style={{ display: "flex", gap: 7, alignItems: "flex-start", padding: "3px 0" }}>
          {r.tone === "warn" && <AlertTriangle size={12} color={colour.warn} style={{ marginTop: 3, flexShrink: 0 }} />}
          <span style={{ fontSize: 12.5, lineHeight: 1.55, color: colour[r.tone] }}>{r.text}</span>
        </div>
      ))}
    </div>
  );
}

export default function DealPanel({ employeeId = null }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError("");
    const q = employeeId ? `?employee=${employeeId}` : "";
    apiGet(`/RevOps/deal-signals/${q}`)
      .then((payload) => !cancelled && setData(payload))
      .catch((err) => !cancelled && setError(err?.message || "Couldn't load deal signals."));
    return () => { cancelled = true; };
  }, [employeeId, attempt]);

  const calls = useMemo(() => data?.calls || [], [data]);

  if (error) return <LoadFailed message={error} onRetry={() => setAttempt((n) => n + 1)} />;
  if (!data) return <p style={muted}>Loading…</p>;

  if (!calls.length) {
    return (
      <div style={card}>
        <p style={{ ...muted, fontSize: 12.5, margin: 0 }}>
          Nothing here yet. Deal signals — when our price landed, whether their own numbers came
          first, who went quiet and for how long — are read off the recording, so they appear once
          calls have been scored from audio. A call entered by hand has no timeline behind it.
        </p>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={card}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5,
                       fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em",
                       color: "var(--c-600)" }}>
          <TrendingUp size={13} /> Deal signals
        </span>
        <p style={{ margin: "5px 0 0", fontSize: 12, ...muted }}>
          About the deal, not about the seller — which is why it is here and not on the scorecard.
          Moments with a time and a name, never a risk score.
        </p>
      </div>

      {calls.map((call) => (
        <div key={call.call_id} style={card}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "baseline" }}>
            <strong style={{ fontSize: 13.5, color: "var(--c-900)" }}>{call.company_name || call.call_label}</strong>
            {call.company_name && call.call_label && (
              <span style={{ fontSize: 11.5, color: "var(--c-500)", marginLeft: 6 }}>{call.call_label}</span>
            )}
            <span style={{ fontSize: 11, ...muted, whiteSpace: "nowrap" }}>
              {call.call_date || "—"} · {call.employee_name}
              {call.duration_seconds ? ` · ${Math.round(call.duration_seconds / 60)} min` : ""}
            </span>
          </div>
          <Timeline call={call} />
          <Readings signals={call.signals || {}} />
        </div>
      ))}
    </div>
  );
}
